import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Form, Space, Tooltip } from 'antd'
import get from 'lodash.get'
import React, { useEffect, useState } from 'react'
import { ACTION_MODE, PAGE_SIZE_OPTIONS } from '../../configData'
import { hasPermission } from '../../config-func'
import Card from '../Card'
import ModalCustom from '../common/Modal/ModalCustom'
import ModalConfirm, { showConfirm } from '../common/ModalConfirm/ModalConfirm'
import openNotification from '../common/Notification/openNotification '
import TableField from '../common/Table/TableField'
import FilterForm from './FilterForm'
import FormPost from './FormPost'
import {
  serviceAdd,
  serviceDelete,
  serviceSearch,
  serviceUpdate
} from '../../services/service-management'
import Loading from '../Loading';

const ServiceManagement = () => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [mode, setMode] = useState(ACTION_MODE.VIEW)
  const [form] = Form.useForm()
  const [dataSource, setDataSource] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  const [filters, setFilters] = useState({
    currentPage: 1,
    pageSize: 10
  })

  const handleSearch = () => {
    if (filters.currentPage === 1) {
      fetchData()
    } else {
      setFilters((prev) => ({ ...prev, currentPage: 1 }))
    }
  }

  async function fetchData() {
    setLoading(true)

    let _dataSource = []
    let _total = 0

    const params = {
      page: filters.currentPage,
      pageSize: filters.pageSize,
      partner_name: filters.partner_name || ''
    }

    const cleanedData = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== '' && value !== null && value !== undefined
      )
    )

    try {
      const response = await serviceSearch(cleanedData)
      _total = response?.data?.total
      _dataSource = response?.data?.list
    } catch (error) {
      openNotification(
        'error',
        error?.response?.data?.message || 'Đã có lỗi xảy ra'
      )
    } finally {
      setLoading(false)
      setTotal(_total)
      setDataSource(_dataSource)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters.currentPage, filters.pageSize, filters.reset])

  const ImageWithFallback = ({ src }) => {
    const [error, setError] = useState(false)

    return error ? (
      <span style={{ color: 'red', fontSize: 14 }}>Không có ảnh</span>
    ) : (
      <img
        src={src}
        alt="Logo"
        style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
        onError={() => setError(true)}
        onClick={() => {
          setPreviewImage(src)
          setPreviewVisible(true)
        }}
      />
    )
  }

  const initialColumns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      fixed: 'left',
      width: 60,
      align: 'center',
      render: (_, __, index) =>
        (filters.currentPage - 1) * filters.pageSize + index + 1
    },
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      render: (url) => <ImageWithFallback src={url} />
    },
    {
      title: 'Tên nhãn hàng',
      dataIndex: 'partner_name',
      key: 'partner_name'
    },
    {
      title: 'Mô tả',
      dataIndex: 'partner_description',
      key: 'partner_description'
    },
    {
      title: 'Link đối tác',
      dataIndex: 'partner_link',
      key: 'partner_link',
      render: (value) => value || '-'
    },
    {
      title: 'Order',
      dataIndex: 'display_order',
      key: 'display_order'
    },
    {
      title: 'Hành động',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          {hasPermission('P83.2', permissions) && (
            <Tooltip title="Chỉnh sửa">
              <Button
                style={{ width: 32 }}
                icon={<EditOutlined />}
                onClick={() => {
                  setIsModalOpen(true)
                  setMode(ACTION_MODE.UPDATE)
                  form.setFieldsValue(record)
                }}
              />
            </Tooltip>
          )}
          {hasPermission('P83.3', permissions) && (
            <Tooltip title="Xoá">
              <Button
                style={{ width: 32 }}
                icon={<DeleteOutlined style={{ color: 'red' }} />}
                onClick={() =>
                  showConfirm({
                    title: 'Xác nhận xoá',
                    content: (
                      <div>
                        Bạn có chắc chắn muốn xoá
                        <b> {record.partner_name}</b> không?
                      </div>
                    ),
                    onOk: () => handleDelete(record)
                  })
                }
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  const handleDelete = async (record) => {
    setLoading(true)
    try {
      const res = await serviceDelete(record.id)
      fetchData()
      openNotification('success', res?.message || 'Xoá thành công')
    } catch (err) {
      const errors = get(err, 'response.data.data', [])
      const errorMessages =
        errors?.map((error) => error.message).join('\n') ||
        get(err, 'response.data.message', [])
      openNotification('error', errorMessages || 'Đã có lỗi xảy ra')
      return true
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const values = await form.validateFields()
      const { id, ...data } = values

      const res =
        mode === ACTION_MODE.ADD
          ? await serviceAdd(data)
          : await serviceUpdate(data, id)

      if (res?.message || res?.data) {
        form.resetFields()
        setIsModalOpen(false)
        fetchData()
        openNotification(
          'success',
          mode === ACTION_MODE.ADD ? 'Tạo thành công !' : 'Cập nhật thành công !'
        )
      } else {
        openNotification('error', 'Đã có lỗi xảy ra')
      }
    } catch (err) {
      const errors = get(err, 'response.data.data', [])
      const errorMessages =
        errors?.map((error) => error.message).join('\n') ||
        get(err, 'response.data.message', [])
      openNotification('error', errorMessages || 'Đã có lỗi xảy ra')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Loading loading={loading} />
      <Card className="mb-2">
        <FilterForm handleSearch={handleSearch} setFilters={setFilters} />
      </Card>
      <Card className="mb-2">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div />
          {hasPermission('P83.1', permissions) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields()
                setMode(ACTION_MODE.ADD)
                setIsModalOpen(true)
              }}
            >
              Thêm mới
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <TableField
          columns={initialColumns}
          data={dataSource}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={{
            current: filters.currentPage,
            pageSize: filters.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS
          }}
          onChange={(pagination) => {
            setFilters((prev) => ({
              ...prev,
              currentPage: pagination.current,
              pageSize: pagination.pageSize
            }))
          }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <ModalCustom
        title={mode === ACTION_MODE.ADD ? 'Thêm dịch vụ' : 'Cập nhật dịch vụ'}
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          form.resetFields()
        }}
        onConfirm={handleSave}
        width={720}
      >
        <FormPost form={form} mode={mode} />
      </ModalCustom>

      <ModalConfirm />

      <ModalCustom
        title="Xem logo"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[]}
        width={520}
      >
        <img alt="Logo" style={{ width: '100%' }} src={previewImage} />
      </ModalCustom>
    </div>
  )
}

export default ServiceManagement
